/**
 * SMSWiser /drops/all fetch sizing.
 *
 * Manual fetch (no date range): honour user `length`, capped at 5000.
 * Date-range fetch: probe 5000 rows → calibrate drops/day → paginate until
 * `from` is covered (no 5000 cap on total estimated rows).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { isoToYmd, type SmswiserDropDateLike } from './drops-date-filter';
import {
  countUniqueDropDays,
  DROPS_CALIBRATION_SAMPLE_SIZE,
  DROPS_PER_DAY_FALLBACK,
  estimateDropsPerDayFromSample,
} from './drops-per-day-calibration';

export { DROPS_CALIBRATION_SAMPLE_SIZE, DROPS_PER_DAY_FALLBACK } from './drops-per-day-calibration';
export {
  countUniqueDropDays,
  estimateDropsPerDayFromSample,
} from './drops-per-day-calibration';

/** Default manual fetch when no date filter. */
export const DROPS_FETCH_LENGTH_DEFAULT = 150;

/** Max rows per SMSWiser request — applies to **manual** fetch only. */
export const DROPS_FETCH_LENGTH_MAX = 5000;

export const DROPS_FETCH_BUFFER_DAYS = 1;

/** Extra rows added to date-range fetch estimates (UI preview + safety margin). */
export const DROPS_FETCH_ROW_BUFFER = 100;

function todayYmdUtc(): string {
  const now = new Date();
  const yyyy = String(now.getUTCFullYear());
  const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(now.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function ymdToUtcMs(ymd: string): number {
  return Date.UTC(
    Number(ymd.slice(0, 4)),
    Number(ymd.slice(5, 7)) - 1,
    Number(ymd.slice(8, 10)),
  );
}

export function inclusiveDaysBack(fromYmd: string, anchorYmd: string): number {
  const fromMs = ymdToUtcMs(fromYmd);
  const anchorMs = ymdToUtcMs(anchorYmd);
  if (Number.isNaN(fromMs) || Number.isNaN(anchorMs)) return 1;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const days = Math.floor((anchorMs - fromMs) / MS_PER_DAY) + 1;
  return Math.max(1, Math.min(days, 365));
}

export interface DropsFetchLengthParams {
  from?: string | null;
  to?: string | null;
  anchor?: string | null;
  /** Calibrated from a 5k sample; omit for UI preview (uses fallback rate). */
  dropsPerDay?: number;
  /** Optional probe batch — when set, calibrates dropsPerDay from it. */
  calibrationSample?: SmswiserDropDateLike[];
}

export interface DropsFetchPlan {
  daysBack: number;
  dropsPerDay: number;
  uniqueDaysInSample: number | null;
  estimatedTotal: number;
  pageSize: number;
  maxPages: number;
}

function resolveAnchorAndDays(params: DropsFetchLengthParams) {
  const fromYmd = isoToYmd(params.from ?? '');
  if (!fromYmd) return null;

  const toYmd = isoToYmd(params.to ?? '');
  const anchorYmd = isoToYmd(params.anchor ?? '') ?? todayYmdUtc();
  const effectiveAnchor = toYmd && toYmd > anchorYmd ? toYmd : anchorYmd;
  if (effectiveAnchor < fromYmd) return null;

  return { fromYmd, daysBack: inclusiveDaysBack(fromYmd, effectiveAnchor) };
}

function resolveDropsPerDay(params: DropsFetchLengthParams): {
  dropsPerDay: number;
  uniqueDaysInSample: number | null;
} {
  if (params.calibrationSample?.length) {
    return {
      dropsPerDay: estimateDropsPerDayFromSample(params.calibrationSample),
      uniqueDaysInSample: countUniqueDropDays(params.calibrationSample),
    };
  }
  if (params.dropsPerDay != null && params.dropsPerDay > 0) {
    return { dropsPerDay: params.dropsPerDay, uniqueDaysInSample: null };
  }
  return { dropsPerDay: DROPS_PER_DAY_FALLBACK, uniqueDaysInSample: null };
}

/** Manual list fetch — capped at {@link DROPS_FETCH_LENGTH_MAX}. */
export function capManualFetchLength(length: number): number {
  const n = Math.max(1, Math.floor(length || 0));
  return Math.min(n, DROPS_FETCH_LENGTH_MAX);
}

/** Date-range plan — total rows uncapped; paginates in 5k SMSWiser pages. */
export function getDropsFetchPlan(params: DropsFetchLengthParams): DropsFetchPlan {
  const resolved = resolveAnchorAndDays(params);
  if (!resolved) {
    return {
      daysBack: 1,
      dropsPerDay: DROPS_PER_DAY_FALLBACK,
      uniqueDaysInSample: null,
      estimatedTotal: DROPS_FETCH_LENGTH_DEFAULT,
      pageSize: DROPS_FETCH_LENGTH_DEFAULT,
      maxPages: 1,
    };
  }

  const { dropsPerDay, uniqueDaysInSample } = resolveDropsPerDay(params);
  const daysWithBuffer = resolved.daysBack + DROPS_FETCH_BUFFER_DAYS;
  const estimatedTotal = daysWithBuffer * dropsPerDay + DROPS_FETCH_ROW_BUFFER;
  const pageSize = DROPS_CALIBRATION_SAMPLE_SIZE;
  const maxPages = Math.max(1, Math.ceil(estimatedTotal / pageSize));

  return {
    daysBack: resolved.daysBack,
    dropsPerDay,
    uniqueDaysInSample,
    estimatedTotal,
    pageSize,
    maxPages,
  };
}

/** Target row count for a date range (uncapped). Uses fallback rate without a sample. */
export function getDropsFetchLength(params: DropsFetchLengthParams): number {
  return getDropsFetchPlan(params).estimatedTotal;
}
